import type { components } from "@/api-schema";
import { PathsV1ModelFamilySearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useModelFamilySearchQuery } from "@/common/api/model-family/use-model-family-search.query";
import { getModelFamilyDatId } from "@/common/helpers/get-model-family-dat-id";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

type QueryBody = components["schemas"]["ModelFamilySearchBody"];

const FILTER_PAGE_SIZE = 50;

function useModelFamilyDatIdFilterData(body: QueryBody): {
  data: FilterData;
  pagination: FilterPaginationState;
} {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: modelFamilies,
    isPending: isLoading,
    error,
  } = useModelFamilySearchQuery({
    options: {
      parameters: {
        extend: [PathsV1ModelFamilySearchPostParametersQueryExtend.ModelFamilyIdentifiers],
        pageSize,
        search: debouncedSearchTerm || undefined,
        searchField: ["modelFamilyIdentifiers.identifier"],
      },
      body,
    },
  });

  const options =
    modelFamilies?.data
      .map((modelFamily) => getModelFamilyDatId(modelFamily.modelFamilyIdentifiers))
      .filter((datId): datId is string => !!datId)
      .map((datId) => ({ id: datId, name: datId }))
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) ?? [];
  const loadedCount = modelFamilies?.data.length ?? 0;
  const total = modelFamilies?.meta.total ?? 0;

  return {
    data: { options, isLoading, error },
    pagination: createPaginationState(total, loadedCount, isLoading),
  };
}

export class ModelFamilyDatIdFilter extends Filter<QueryBody, unknown> {
  readonly id = "identifier";
  readonly labelSingular = "masterdata:DATID";
  readonly labelPlural = "masterdata:DATID";
  readonly placeholder = "masterdata:DATID";
  readonly searchPlaceholder = "masterdata:DATID";
  readonly emptyText = "common:NoOptionsFound";

  constructor({ body = {} }: { body?: Partial<QueryBody> } = {}) {
    super();
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <ModelFamilyDatIdFilterComponent filter={this} {...props} />;
  }
}

function ModelFamilyDatIdFilterComponent({ filter, ...props }: { filter: ModelFamilyDatIdFilter } & BaseFilterProps) {
  const { data, pagination } = useModelFamilyDatIdFilterData(filter.body);
  const filterWithData = Object.create(filter);
  filterWithData.getData = () => ({
    ...data,
    options: [
      ...data.options,
      ...props.selectedValues
        .filter((datId) => !data.options.some((option) => option.id === datId))
        .map((datId) => ({ id: datId, name: datId })),
    ],
  });

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
